/**
 * What recovery needs to survive a reload, per workspace and actor: the card
 * title of each operation this browser created, and every create or Undo
 * whose answer never arrived, with the Idempotency-Key it was sent with.
 * Replaying that key returns the same operation instead of doing the work
 * twice (F3). Storage can be unavailable (private mode, full quota); then
 * recovery relies on the server list alone.
 */

export type tPendingWrite =
  | { kind: 'create'; key: string; previewId: string; title: string; at: number }
  | { kind: 'undo'; key: string; operationId: string; title: string; at: number };

export type tStoredOperations = {
  titles: Record<string, { title: string; at: number }>;
  pending: tPendingWrite[];
};

/** A replayed write is only worth trying while its preview can still be used. */
export const PENDING_TTL_MS = 5 * 60_000;
const TITLE_TTL_MS = 24 * 60 * 60_000;

export function operationsStorageKey(workspaceId: string, actorId: string): string {
  return `ls.directory.operations.${workspaceId}.${actorId}`;
}

export function readStoredOperations(key: string): tStoredOperations {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as Partial<tStoredOperations>) : {};
    return {
      titles: parsed.titles && typeof parsed.titles === 'object' ? parsed.titles : {},
      pending: Array.isArray(parsed.pending) ? parsed.pending : [],
    };
  } catch {
    return { titles: {}, pending: [] };
  }
}

/** Applies `update` to the stored state, dropping entries past their TTL. */
export function updateStoredOperations(
  key: string,
  update: (state: tStoredOperations) => tStoredOperations,
): void {
  const now = Date.now();
  const current = readStoredOperations(key);
  const pruned: tStoredOperations = {
    titles: Object.fromEntries(
      Object.entries(current.titles).filter(([, entry]) => now - entry.at < TITLE_TTL_MS),
    ),
    pending: current.pending.filter((write) => now - write.at < PENDING_TTL_MS),
  };
  try {
    localStorage.setItem(key, JSON.stringify(update(pruned)));
  } catch {
    // Private mode or full storage: recovery then relies on the server list.
  }
}
